import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import {
  listAllGroupConfigurations,
  createGroupConfiguration,
  deleteGroupConfiguration,
} from "../../api/groupConfigurationApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const classApi = createLookupApi("classes");
const groupApi = createLookupApi("groups");

/**
 * digicampusbd's "Initial Setup > Group Configuration" screen
 * (`initial-setup-group-configuration.html`) — assign one or more Groups to
 * a Class, list the existing assignments, remove one. Same underlying
 * `/api/group-configurations` resource as the Online Admission module's own
 * "Group Settings" tab (`OnlineAdmissionSettingsPage`), but that screen also
 * edits the online-admission application fee, a field the old app's plain
 * Initial Setup page never showed (commented out in its own template) — kept
 * as two separate pages here for the same reason the old app had two.
 */
export default function GroupConfigurationPage() {
  const [classes, setClasses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classId, setClassId] = useState("");
  const [groupIds, setGroupIds] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = () => {
    setLoading(true);
    listAllGroupConfigurations()
      .then(setConfigs)
      .catch(() => setError("Failed to load group configurations."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    classApi.list().then(setClasses);
    groupApi.list().then(setGroups);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleGroup = (id) => setGroupIds((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));

  const handleAssign = async () => {
    setError("");
    if (!classId || groupIds.length === 0) {
      setError("Select a class and at least one group.");
      return;
    }
    try {
      await createGroupConfiguration({ classId: Number(classId), groupIds: groupIds.map(Number) });
      setSuccess("Group configuration created.");
      setClassId("");
      setGroupIds([]);
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to create group configuration.");
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove "${row.classGroupName}"?`)) return;
    try {
      await deleteGroupConfiguration(row.id);
      setSuccess("Group configuration removed.");
      setConfigs((prev) => prev.filter((c) => c.id !== row.id));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Group Configuration</h1>
      </div>
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <h3>Assign Groups to a Class</h3>
      <div className="attendance-filters">
        <label>
          Class
          <select value={classId} onChange={(e) => setClassId(e.target.value)}>
            <option value="">Select class</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div style={{ margin: "12px 0" }}>
        {groups.map((g) => (
          <label key={g.id} className="checkbox-label" style={{ display: "inline-flex", marginRight: 12 }}>
            <input type="checkbox" checked={groupIds.includes(g.id)} onChange={() => toggleGroup(g.id)} /> {g.name}
          </label>
        ))}
      </div>
      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleAssign}>
          Assign
        </button>
      </div>

      <h3 style={{ marginTop: 24 }}>Existing Configurations</h3>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[{ key: "classGroupName", label: "Class (Group)" }]}
          rows={configs}
          actions={(row) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
              Remove
            </button>
          )}
        />
      )}
    </MainLayout>
  );
}
